import { ApolloQueryResult, gql } from '@apollo/client';
import { PromoCode, SalaryAmountType } from '__generated__/graphql';
import { notification, Spin } from 'antd';
import { calculatedDeliveryType } from 'components/jobSubmission/deliveryType';
import { toActivationDateInput } from 'components/jobSubmission/formComponents/topResultActivationDate';
import client from 'graphql/client';
import translate from 'languageProvider/inline';
import { JobProps } from 'types/jobSubmission.types';

const fetchExternalLinkPackages = () => {
  const query = gql`
    query externalLink {
      companyExternalLinkPackages
    }
  `;

  return client.query({
    query,
    fetchPolicy: 'network-only',
  });
};

const fetchIncompleteJobGraphQl = (hash: string) => {
  const query = gql`
    query incompleteJob($hash: String!) {
      incompleteJob(hash: $hash) {
        durationInDays
        adType
        startDate
        endDate
        companyName
        adText
        position
        availability
        location
        salaryFrom
        salaryTo
        salaryType
        currency
        externalLink
        applicationEmail
        phoneNumber
        email
        note
        deliveryType
        highlighted
        topResult
        topResultActivationDate
        payType
        workingHours
        employmentType
        workFromHome
        premiumText
        package
        adTextLength
        preselectionQuestions {
          question
          commentAllowed
          order
          hidden
          allowMulti
          aiGenerated
          preselectionQuestionAnswers {
            id
            answer
            order
            hidden
            points
          }
        }
        shouldCreatePreselectionTemplateQuestions
        logo {
          type
          file {
            path
          }
          url
        }
        document {
          file {
            path
          }
          url
        }
        pib
        mbr
        billingCompanyName
        contactName
        contactPhone
        address
        postalCode
        cityId
        city
        countryId
        invoiceEmail
        employerType
        employerTypeValue
        paymentType
        cardOwnerName
        proformaEmailCheckbox
        proformaEmail
        testAd
        workplaceAddress {
          city
          address
        }
        shift
        onlineInterview
        supportsReturnFromAbroad
        siteVisibility
        cvRequired
        confirmedIt
        socialPosting {
          postingSite
          socialNetwork
          type
        }
        invite
        renewedFrom
        wantsToEditAdText
        projectId
        hybridWork
        candidatePackage
        promoCode
        numberOfExecutors
        regions
        fieldwork
        benefits
        smartSort
        coverPhotoFile {
          id
          path
          url
          thumbnailUrl
        }
        applicationStatusConsent
        amount
        preselectionModalShown
      }

      employerCompany {
        id
        hasOnlineInterview
        hasWelcomePackage
        hasSpecialOffer
        welcomePackageExpirationDateTime
        smartSortSubscription {
          id
          activeFrom
          activeTo
          active
        }
      }
    }
  `;

  const variables = {
    hash,
  };

  return client.query({
    query,
    variables,
    fetchPolicy: 'network-only',
  });
};

const fetchLists = async (recache: boolean) => {
  const params: any = {};
  const query = gql`
    query cititesCountriesAdvertPositionsMinimumSalary {
      lists {
        cities {
          id
          name
        }
        advertPositions {
          id
          name
        }
        countries {
          id
          name
        }
        regions {
          id
          name
        }
        jobBenefits {
          id
          name
        }
      }
      minimumSalaryInSerbia {
        hourly
        monthly
      }
    }
  `;

  if (recache) {
    params.fetchPolicy = 'network-only';
  }

  return client.query({
    query,
    ...params,
  });
};

let logoRef: any;
let documentRef: any;

/** Autosave payload: the same form values, with the activation day already in the gateway wire format. */
type IncompleteJobVariables = Omit<JobProps, 'topResultActivationDate'> & {
  topResultActivationDate?: string | null;
};

const modifyIncompleteJobGraphQl = (variables: JobProps) => {
  const requestVariables: IncompleteJobVariables = {
    ...variables,
    topResultActivationDate: toActivationDateInput(variables.topResultActivationDate),
  };
  const startDate = variables.dateRange.startValue;
  const endDate = variables.dateRange.endValue;

  requestVariables.startDate = startDate?.format('Y-MM-DD');
  requestVariables.endDate = endDate?.format('Y-MM-DD');

  requestVariables.salaryType = variables.salaryType || SalaryAmountType.Net;

  requestVariables.deliveryType = calculatedDeliveryType(variables);

  /**
   * remove logo from payload if it didnt change
   */
  if (logoRef !== requestVariables.logo) {
    logoRef = requestVariables.logo;
  } else {
    delete requestVariables.logo;
  }

  /**
   * remove document from payload if it didnt change
   */
  if (documentRef !== requestVariables.document) {
    documentRef = requestVariables.document;
    if (requestVariables.document) {
      notification.open({
        key: 'upload-notification',
        title: translate('jobSubmission.field.text.upload.message'),
        description: translate('jobSubmission.field.text.upload.description'),
        icon: <Spin />,
        closeIcon: ' ',
        duration: 0,
      });
    }
  } else {
    delete requestVariables.document;
  }

  if (requestVariables.coverPhotoFile) {
    requestVariables.coverPhotoFileId = requestVariables.coverPhotoFile.id;
  }

  /**
   * Strip IDs from preselection questions
   */
  if (requestVariables.preselectionQuestions) {
    requestVariables.preselectionQuestions = requestVariables.preselectionQuestions.map(
      (question: any) => {
        if (question.id) {
          const { id, ...questionWithoutId } = question;
          return questionWithoutId;
        }
        return question;
      }
    );
  }

  const mutation = gql`
    mutation mutateIncompleteJob(
      $uid: String
      $durationInDays: Int
      $currency: Currency
      $adType: Int
      $startDate: String
      $endDate: String
      $payType: PayType
      $workingHours: Int
      $employmentType: Int
      $paymentType: PaymentType
      $companyName: String
      $adText: String
      $position: String
      $availability: [AvailabilityType]
      $location: [String]
      $salaryFrom: String
      $salaryTo: String
      $salaryType: SalaryAmountType
      $externalLink: String
      $email: String
      $applicationEmail: String
      $phoneNumber: String
      $note: String
      $deliveryType: [Int]
      $highlighted: Boolean
      $topResult: Boolean
      $topResultActivationDate: String
      $workFromHome: Boolean
      $premiumText: String
      $package: Int
      $adTextLength: Int
      $logo: LogoInput
      $document: DocumentInput
      $pib: String
      $mbr: String
      $billingCompanyName: String
      $contactName: String
      $contactPhone: String
      $address: String
      $postalCode: String
      $cityId: String
      $city: String
      $countryId: String
      $invoiceEmail: String
      $employerType: Int
      $employerTypeValue: String
      $cardOwnerName: String
      $proformaEmailCheckbox: Boolean
      $proformaEmail: String
      $testAd: Boolean
      $reachedPage: Int
      $cvRequired: Boolean
      $workplaceAddress: [WorkplaceAddressInput]
      $shift: Int
      $onlineInterview: Boolean
      $applicationStatusConsent: Boolean
      $supportsReturnFromAbroad: Boolean
      $siteVisibility: Int
      $confirmedIt: Boolean
      $socialPosting: [SocialPostingInput]
      $invite: Boolean
      $renewedFrom: Int
      $wantsToEditAdText: Boolean
      $projectId: Int
      $hybridWork: Boolean
      $fieldwork: Boolean
      $candidatePackage: CandidatePackageTypeEnum
      $promoCode: String
      $numberOfExecutors: Int
      $regions: [Int]
      $benefits: [Int]
      $smartSort: Boolean
      $coverPhotoFileId: Int
      $preselectionQuestions: [PreselectionQuestionInput]
      $shouldCreatePreselectionTemplateQuestions: Boolean
      $amount: Int
      $preselectionModalShown: Boolean
    ) {
      mutateIncompleteJob(
        uid: $uid
        durationInDays: $durationInDays
        currency: $currency
        adType: $adType
        startDate: $startDate
        endDate: $endDate
        payType: $payType
        workingHours: $workingHours
        employmentType: $employmentType
        paymentType: $paymentType
        companyName: $companyName
        adText: $adText
        position: $position
        availability: $availability
        location: $location
        salaryFrom: $salaryFrom
        salaryTo: $salaryTo
        salaryType: $salaryType
        externalLink: $externalLink
        email: $email
        applicationEmail: $applicationEmail
        phoneNumber: $phoneNumber
        note: $note
        deliveryType: $deliveryType
        highlighted: $highlighted
        topResult: $topResult
        topResultActivationDate: $topResultActivationDate
        workFromHome: $workFromHome
        premiumText: $premiumText
        package: $package
        adTextLength: $adTextLength
        logo: $logo
        document: $document
        pib: $pib
        mbr: $mbr
        billingCompanyName: $billingCompanyName
        contactName: $contactName
        contactPhone: $contactPhone
        address: $address
        postalCode: $postalCode
        cityId: $cityId
        city: $city
        countryId: $countryId
        invoiceEmail: $invoiceEmail
        employerType: $employerType
        employerTypeValue: $employerTypeValue
        cardOwnerName: $cardOwnerName
        proformaEmailCheckbox: $proformaEmailCheckbox
        proformaEmail: $proformaEmail
        testAd: $testAd
        reachedPage: $reachedPage
        cvRequired: $cvRequired
        workplaceAddress: $workplaceAddress
        shift: $shift
        onlineInterview: $onlineInterview
        applicationStatusConsent: $applicationStatusConsent
        supportsReturnFromAbroad: $supportsReturnFromAbroad
        siteVisibility: $siteVisibility
        confirmedIt: $confirmedIt
        socialPosting: $socialPosting
        invite: $invite
        renewedFrom: $renewedFrom
        wantsToEditAdText: $wantsToEditAdText
        projectId: $projectId
        hybridWork: $hybridWork
        candidatePackage: $candidatePackage
        promoCode: $promoCode
        numberOfExecutors: $numberOfExecutors
        regions: $regions
        fieldwork: $fieldwork
        benefits: $benefits
        smartSort: $smartSort
        coverPhotoFileId: $coverPhotoFileId
        preselectionQuestions: $preselectionQuestions
        shouldCreatePreselectionTemplateQuestions: $shouldCreatePreselectionTemplateQuestions
        amount: $amount
        preselectionModalShown: $preselectionModalShown
      )
    }
  `;

  const response = client.mutate({
    mutation,
    variables: requestVariables,
  });

  response.then(() => {
    notification.destroy('upload-notification');
  });

  return response;
};

const insertJobMutation = (variables: any) => {
  const mutation = gql`
    mutation (
      $title: String!
      $companyName: String!
      $datePosted: String!
      $expireDate: String!
      $duration: Int!
      $emailContact: String
      $textAd: String!
      $currency: String
      $applicationDeliveryEmail: String
      $billingContactName: String
      $billingCompanyName: String
      $pib: String
      $phone: String
      $zipCode: String
      $address: String
      $idNumber: String
      $disabled: Boolean
      $students: Boolean
      $cities: [Int]
      $workFromHome: Boolean
      $package: Int
      $proformaEmail: String
      $workingHours: WorkingHours!
      $employmentType: EmploymentType!
      $jobType: JobType!
      $logo: LogoInput
      $document: DocumentInput
      $shortDescription: String
      $salaryFrom: String
      $salaryTo: String
      $salaryAdditional: String
      $salaryType: SalaryAmountType
      $additionalNote: String
      $cardOwnerName: String
      $countryId: Int
      $cityId: Int
      $cityName: String
      $employerType: Int
      $paymentMethod: PaymentMethod!
      $addonsPaymentMethod: PaymentMethod!
      $incompleteHash: String
      $locationOverride: String
      $locationNormalized: String
      $externalApplicationsLink: String
      $applicationDeliveryMethod: ApplicationDeliveryMethod!
      $applicationPhoneNumber: String
      $highlighted: Highlighted
      $highlightedPackageId: Int
      $topResult: Boolean
      $topResultActivationDate: String
      $totalPriceAmount: Int
      $totalPriceCurrency: Currency
      $testAd: Boolean
      $cvRequired: Boolean
      $workplaceAddress: [WorkplaceAddressInput]
      $shift: Int
      $onlineInterview: Boolean
      $applicationStatusConsent: Boolean
      $supportsReturnFromAbroad: Boolean
      $siteVisibility: Int
      $jobSocialPosting: [SocialPostingInput]
      $invite: Boolean
      $inviteEligible: Boolean
      $projectId: Int
      $hybridWork: Boolean
      $candidatePackage: CandidatePackageTypeEnum
      $promoCode: String
      $promoCodeServices: [Int]
      $numberOfExecutors: Int
      $benefits: [Int]
      $smartSort: Boolean
      $coverPhotoFileId: Int
      $regions: [Int]
      $fieldwork: Boolean
      $preselectionQuestions: [PreselectionQuestionInput]
      $shouldCreatePreselectionTemplateQuestions: Boolean
      $amount: Int
      $source: String
    ) {
      insertJob(
        job: {
          title: $title
          companyDisplayName: $companyName
          datePosted: $datePosted
          expireDate: $expireDate
          duration: $duration
          jobType: $jobType
          emailContact: $emailContact
          textAd: $textAd
          salaryAmount: {
            from: $salaryFrom
            to: $salaryTo
            additional: $salaryAdditional
            type: $salaryType
          }
          salaryCurrency: $currency
          applicationDeliveryMethod: $applicationDeliveryMethod
          applicationDeliveryEmail: $applicationDeliveryEmail
          externalApplicationsLink: $externalApplicationsLink
          applicationPhoneNumber: $applicationPhoneNumber
          crmJobPackageId: $package
          cities: $cities
          employmentType: $employmentType
          workFromHome: $workFromHome
          forInvalids: $disabled
          forStudents: $students
          shortDescription: $shortDescription
          highlighted: $highlighted
          highlightedPackageId: $highlightedPackageId
          topResult: $topResult
          topResultActivationDate: $topResultActivationDate
          proformaEmail: $proformaEmail
          workingHours: $workingHours
          logo: $logo
          document: $document
          additionalNote: $additionalNote
          paymentMethod: $paymentMethod
          addonsPaymentMethod: $addonsPaymentMethod
          incompleteHash: $incompleteHash
          locationOverride: $locationOverride
          locationNormalized: $locationNormalized
          totalPrice: { amount: $totalPriceAmount, currency: $totalPriceCurrency }
          testAd: $testAd
          cvRequired: $cvRequired
          workplaceAddress: $workplaceAddress
          shift: $shift
          onlineInterview: $onlineInterview
          applicationStatusConsent: $applicationStatusConsent
          supportsReturnFromAbroad: $supportsReturnFromAbroad
          siteVisibility: $siteVisibility
          useNewSiteVisibility: true
          jobSocialPosting: $jobSocialPosting
          invite: $invite
          inviteEligible: $inviteEligible
          projectId: $projectId
          hybridWork: $hybridWork
          candidatePackage: $candidatePackage
          promoCodeServices: $promoCodeServices
          numberOfExecutors: $numberOfExecutors
          benefits: $benefits
          smartSort: $smartSort
          coverPhotoFileId: $coverPhotoFileId
          regions: $regions
          fieldwork: $fieldwork
          preselectionQuestions: $preselectionQuestions
          shouldCreatePreselectionTemplateQuestions: $shouldCreatePreselectionTemplateQuestions
          amount: $amount
        }
        paymentDetails: {
          contactName: $billingContactName
          companyName: $billingCompanyName
          pib: $pib
          phone: $phone
          zipCode: $zipCode
          address: $address
          idNumber: $idNumber
          cardOwnerName: $cardOwnerName
          countryId: $countryId
          cityId: $cityId
          cityName: $cityName
          employerType: $employerType
          promoCode: $promoCode
          source: $source
        }
      ) {
        id
        receiptId
      }
    }
  `;

  return client.mutate({
    mutation,
    variables,
  });
};

const fetchAddonPricelist = (variables: { currency: String; adPlacementType: string }) => {
  const query = gql`
    query addonPriceList($currency: Currency!, $adPlacementType: AdPlacementType) {
      addonPriceList(currency: $currency, adPlacementType: $adPlacementType) {
        price {
          amount
          currency
        }
        tax {
          amount
          currency
        }
        priceWithTax {
          amount
          currency
        }
        name
      }
    }
  `;

  return client.query({
    query,
    variables,
  });
};

export const fetchIsItPositionGraphql = (variables: { search: String }) => {
  const query = gql`
    query isITPosition($search: String) {
      isITPosition(search: $search)
    }
  `;

  return client.query({
    query,
    variables,
  });
};

export const fetchSrpozMatcherGraphql = (variables: { title: String; cityId: number }) => {
  const query = gql`
    query eligibleForInvites($title: String, $cityId: Int) {
      eligibleForInvites(title: $title, cityId: $cityId) {
        eligible
        candidatesCount
      }
    }
  `;

  return client.query({
    query,
    variables,
  });
};

export const fetchPreviousProjects = () => {
  const query = gql`
    query previousProjects {
      projects(sortDirection: "desc", sortField: "p.id") {
        project {
          id
          name
          timeCreated
          accessType
        }
      }
    }
  `;

  return client.query({
    query,
  });
};

export const SOCIAL_NETWORK_PACKAGES = gql`
  query socialNetworkPackageOptions {
    socialNetworkPackages {
      id
      balance
      active
      type {
        id
        socialNetwork
        size
        website
        name
      }
    }
  }
`;

export const validateExternalPromoCode = (
  code: string
): Promise<ApolloQueryResult<{ promotion: PromoCode }>> => {
  const query = gql`
    query validatePromoCode($code: String!) {
      promotion(code: $code) {
        id
        code
        type
        services {
          id
          service
          serviceType
          balance
          discountPercentage
          availableForUse
        }
      }
    }
  `;

  return client.query({
    query,
    variables: { code },
  });
};

export const HIGHLIGHTED_PACKAGES = gql`
  query highlightedPackages {
    highlightedPackages {
      id
      type {
        id
      }
      balance
      active
      activeToDate
    }
  }
`;

export const TOP_RESULT_PACKAGES = gql`
  query topResultPackages {
    topResultPackages {
      id
      type {
        id
      }
      balance
      active
      activeToDate
    }
  }
`;

export const AI_QUESTIONS_GENERATING_REMAINING = gql(`
    query preselectionAiSuggestionJobIncompleteLimit($jobIncompleteUid: String!){
      preselectionAiSuggestionJobIncompleteLimit(jobIncompleteUid: $jobIncompleteUid) {
        tokens
        maxTokens
      }
}
  `);

export {
  fetchAddonPricelist,
  fetchExternalLinkPackages,
  fetchIncompleteJobGraphQl,
  fetchLists,
  insertJobMutation,
  modifyIncompleteJobGraphQl,
};
